import type { JSX } from 'react';
import { cn } from '../lib/cn.js';
import { ConfirmDialog, templateRemoveConfirm } from './ConfirmDialog.js';
import { IconClose } from '../icons/index.js';
import { raisonDeRetraitDuModele } from '../lib/templates.js';
import type { ModeleDeFiche } from '../lib/templates.js';

/**
 * LE MENU DEROULANT DES MODELES, la moitie droite du BOUTON SCINDE « Ajouter »
 * (rangee 3a). Il remplace la fenetre de choix « Depuis un modele » posee le
 * meme jour : decision du fondateur du 07/09/2026, un bouton scinde comme le
 * « Nouveau » de Notion, partie gauche l'ajout normal, fleche bas les modeles.
 *
 * CE QU'IL FAIT, ET CE QU'IL NE FAIT PAS. Il montre les modeles du jeu de la
 * porte et rend le choix ; il n'ecrit rien, n'appelle aucun port et ne connait
 * pas le SDK. Choisir et retirer sont deux callbacks que le CHASSIS tient.
 * CHOISIR N'ECRIT RIEN NON PLUS, depuis le 23/09/2026 (fondateur) : le choix
 * ouvre la fiche EN CREATION, pre-remplie par le modele (le brouillon de la
 * regle G6), et c'est « Creer » qui ecrit. Il n'y a donc ni attente ni echec a
 * montrer ici : la ligne choisie ferme le menu, et la fiche est la.
 *
 * CHAQUE LIGNE DIT CE QUE LE MODELE EMPORTE (chantier `modeles-enfants`) : sous
 * le nom, la ligne attenuee porte la description ET/OU le nombre de fiches
 * liees (« 3 fiches liees »), parce que « Creer » les creera avec la fiche, et
 * que la fiche en creation les montre en apercu. Le calcul et son pourquoi :
 * `compteDesFichesLiees`, dans ce fichier.
 *
 * IL S'OUVRE MEME VIDE, et il MONTRE alors ou se fabrique un modele : deux
 * libelles, le manque puis le chemin (G25, G26). C'est le changement de
 * doctrine du bouton scinde : le second chemin de creation n'est plus CACHE
 * quand la porte n'a aucun modele (G16, on grise ou on explique, on ne cache
 * pas). Un panneau vide et muet ferait chercher dans les reglages un geste qui
 * vit dans le menu d'une fiche.
 *
 * LE VOCABULAIRE EST CELUI DU MENU « ... » DE LA FICHE (`RecordNavGroup`,
 * `ScreenLayout.tsx`) : meme panneau (`--shadow-pop`, `--radius-md`, retrait de
 * `p-1`), meme `role="menu"`, memes items, meme entree inerte en `aria-disabled`
 * quand il n'y a rien a proposer. Deux menus dessines deux fois dans le meme
 * produit finissent par ne plus se ressembler.
 */
export interface TemplateMenuProps {
  /** L'id que le chevron pointe en `aria-controls`. */
  id: string;
  /**
   * Le libelle de la PORTE active (`tools.door.label`), pour l'en-tete. Absent,
   * l'en-tete dit « cette porte » : le menu nomme toujours ce qu'il liste,
   * sinon on ne sait pas pourquoi les modeles d'a cote n'y sont pas.
   */
  doorLabel?: string;
  /** Les modeles du jeu de la porte, dans l'ordre rendu par le port. */
  modeles: readonly ModeleDeFiche[];
  /**
   * OUVRIR LA FICHE EN CREATION, PRE-REMPLIE par ce modele (regle G6) : le
   * chassis appelle `tools.ouvrirBrouillon({ depuisModele })` et referme le
   * menu. SYNCHRONE : rien n'est ecrit, il n'y a ni attente ni echec a montrer.
   */
  onChoose: (modele: ModeleDeFiche) => void;
  /**
   * DEMANDER LE RETRAIT de ce modele. Le chassis ouvre la confirmation et ferme
   * le menu : un seul panneau a la fois, la doctrine du socle sur les overlays.
   */
  onRemove: (modele: ModeleDeFiche) => void;
}

/**
 * CE QUE LE MODELE EMPORTE, EN TOUTES LETTRES : « 1 fiche liee », « 3 fiches
 * liees », ou `''` quand il n'en emporte aucune.
 *
 * POURQUOI CE NOMBRE EST DANS LE MENU, ET PAS AILLEURS. « Ajouter ▾ » cree la
 * fiche ET ses fiches liees d'un seul geste (`instantiate`, chantier
 * `modeles-enfants`). Sans lui, deux modeles au nom voisin se ressemblent
 * jusqu'au moment ou l'un fabrique une fiche seule et l'autre une fiche avec ses
 * trois interventions : la difference ne se voit qu'APRES, sur une fiche qu'il
 * faut ensuite defaire a la main.
 *
 * ZERO NE SE DIT PAS. « 0 fiche liee » sous chaque modele ordinaire ferait une
 * ligne de bruit sous tous les noms, pour un fait qui est le cas ordinaire (un
 * modele d'avant ce chantier, ou enregistre depuis un brouillon, n'en porte
 * aucune). L'absence de mention EST le zero.
 *
 * FONCTION PURE ET EXPORTEE EXPRES : un pluriel qui se trompe ne fait rougir
 * aucun build, et personne ne relit une ligne attenuee de 11 pixels.
 */
export function compteDesFichesLiees(modele: ModeleDeFiche): string {
  const enfants = modele.children?.length ?? 0;
  if (enfants === 0) return '';
  return enfants > 1 ? `${enfants} fiches liées` : '1 fiche liée';
}

export function TemplateMenu({
  id,
  doorLabel,
  modeles,
  onChoose,
  onRemove,
}: TemplateMenuProps): JSX.Element {
  /*
   * PLUS D'ETAT D'ECRITURE DANS CE MENU (23/09/2026) : il portait une ligne
   * « Creation... », des lignes eteintes pendant l'ecriture et un « Ca n'a pas
   * marche » en pied. Le choix n'ecrit plus rien, il ouvre la fiche a creer :
   * l'attente et l'echec vivent desormais ou vit l'ecriture, sur « Creer » et
   * dans la pastille de la rangee 3, comme pour « + Ajouter ».
   */
  return (
    <div
      id={id}
      role="menu"
      aria-label="Modèles"
      className={cn(
        'absolute left-0 top-[calc(100%+0.4375rem)] z-20 flex min-w-64 max-w-80 flex-col p-1',
        'rounded-md border border-border bg-surface shadow-pop',
      )}
    >
      {/* L'EN-TETE NOMME LE JEU LISTE, il ne se clique pas. Sans lui, une
          personne qui a fabrique un modele sur une AUTRE porte le chercherait
          ici : les modeles sont ranges PAR JEU, et c'est cette ligne qui le
          dit. */}
      <p
        role="none"
        className="truncate px-2 py-1.5 font-sans text-[0.6875rem] font-medium text-ink-400"
      >
        Modèles de {doorLabel != null && doorLabel !== '' ? doorLabel : 'cette porte'}
      </p>
      {modeles.length === 0 ? (
        /* L'ETAT VIDE, DEUX LIBELLES ET RIEN DE PLUS (G25, G26). Le manque se
           nomme NU (« Aucun modele »), et le geste ne se RACONTE pas : le
           chemin pour fabriquer un modele s'ECRIT comme un chemin, ses etapes
           separees par des chevrons, sans verbe a l'imperatif ni ponctuation de
           phrase. Trois lignes de prose expliquaient ici ce qu'un chemin de
           quatre mots montre : elles envoyaient LIRE une consigne la ou on
           cherche un endroit.
           La forme reste celle du menu « ... » de la fiche (« Aucune action
           pour l'instant ») : UNE entree inerte, jamais un panneau muet, et
           deux lignes dans le meme item comme un modele en porte deux (son nom,
           puis son sous-titre en encre attenuee).
           `aria-disabled` et NON `disabled` : un bouton `disabled` ne recoit
           aucun evenement de souris, il sort de l'ordre de tabulation, et le
           chemin deviendrait inatteignable au clavier. */
        <button
          type="button"
          role="menuitem"
          aria-disabled
          className={cn(
            'flex min-h-8 min-w-0 cursor-default flex-col justify-center rounded-sm px-2 py-1 text-left',
            'font-sans text-[0.8125rem] leading-normal',
          )}
        >
          <span className="font-medium text-ink-300">Aucun modèle</span>
          {/* LE CHEMIN, en encre attenuee et a la taille du menu, SUR UNE SEULE
              LIGNE : un chemin qui se casse en deux ne se lit plus comme un
              chemin (le retour tombait au milieu du nom de l'entree). Il est
              LITTERAL, donc sa largeur est connue et tient sous `max-w-80` : le
              panneau s'elargit pour lui, et rien n'est jamais tronque. */}
          <span className="whitespace-nowrap font-normal text-ink-400">
            Fiche › menu … › Enregistrer comme modèle
          </span>
        </button>
      ) : (
        modeles.map((modele) => {
          const description = modele.description == null ? '' : modele.description.trim();
          const compte = compteDesFichesLiees(modele);
          /* UN MODELE CITE PAR UN PLAN RECURRENT NE SE RETIRE PAS (chantier
             `plan-sur-modele`) : la croix reste la, ETEINTE, et dit pourquoi au
             survol (G16). La cachee ferait chercher le geste ailleurs. */
          const retraitRaison = raisonDeRetraitDuModele(modele);
          return (
            /* `role="none"` sur la coque : le menu ne compte que ses ITEMS,
               et une ligne en porte DEUX (choisir, retirer). Sans ca, la boite
               de mise en page entrerait dans l'arbre d'accessibilite entre le
               menu et ses entrees. */
            <div key={modele.id} role="none" className="flex items-center gap-0.5">
              {/* LA LIGNE ENTIERE CHOISIT : le nom n'est pas une etiquette a
                  cote d'un bouton, c'est le geste lui-meme. Un menu ou il faut
                  viser un second bouton pour faire ce que la ligne annonce est
                  un menu qu'on rate une fois sur deux. */}
              <button
                type="button"
                role="menuitem"
                title={modele.name}
                onClick={() => onChoose(modele)}
                className={cn(
                  'flex min-h-8 min-w-0 flex-1 flex-col justify-center rounded-sm px-2 py-1 text-left',
                  'font-sans transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-border',
                  'cursor-pointer text-ink-700 hover:bg-surface-1 hover:text-ink-900',
                )}
              >
                <span className="truncate text-[0.8125rem] font-medium">{modele.name}</span>
                {/* LA LIGNE ATTENUEE : la description, le nombre de fiches
                    liees, ou les deux. Elle ne se rend que si elle dit quelque
                    chose : une ligne vide sous chaque nom ferait un menu deux
                    fois plus haut pour rien.
                    LE COMPTE NE SE COUPE JAMAIS, C'EST LA DESCRIPTION QUI SE
                    TRONQUE (meme regle que le « · obligatoire » de la fiche) :
                    le panneau plafonne a `max-w-80`, et une description un peu
                    longue mangeait exactement les trois mots qu'on est venu
                    lire. Une description tronquee reste lisible ; un compte
                    tronque n'existe pas. */}
                {(description !== '' || compte !== '') && (
                  <span className="flex min-w-0 items-baseline gap-1 text-[0.6875rem] font-normal text-ink-400">
                    {description !== '' && <span className="truncate">{description}</span>}
                    {compte !== '' && (
                      /* LE POINT MEDIAN SEPARE DEUX FAITS DE MEME RANG : ni
                         parenthese (qui hierarchise), ni tiret (le cadratin est
                         interdit dans ce depot, et le court se lit comme un
                         signe moins devant un nombre). */
                      <span className="shrink-0">{description === '' ? compte : `· ${compte}`}</span>
                    )}
                  </span>
                )}
              </button>
              {/* LA CROIX RETIRE, comme sur un tag de la zone 4a : dans ce
                  produit, la croix sur une entree de liste veut dire « retirer
                  celle-ci », et elle ne veut jamais dire autre chose. Elle
                  passe par la fenetre de confirmation du chassis, jamais par un
                  retrait sec. */}
              <button
                type="button"
                role="menuitem"
                title={retraitRaison ?? 'Retirer ce modèle'}
                aria-label="Retirer ce modèle"
                {...(retraitRaison == null
                  ? { onClick: () => onRemove(modele) }
                  : { 'aria-disabled': true })}
                className={cn(
                  'flex h-6 w-6 shrink-0 items-center justify-center rounded-sm border-none bg-transparent transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-border',
                  retraitRaison == null
                    ? 'cursor-pointer text-ink-400 hover:bg-surface-3 hover:text-ink-900'
                    : 'cursor-default text-ink-300',
                )}
              >
                <IconClose size={10} />
              </button>
            </div>
          );
        })
      )}
    </div>
  );
}

/**
 * LA CONFIRMATION DE RETRAIT, ecrite ici (avec le menu qui la demande) et
 * MONTEE A LA RACINE de l'arbre par le chassis, jamais dans la rangee 3 :
 * celle-ci est `relative z-10`, donc elle ouvre un contexte d'empilement dont
 * le voile d'un `Modal` ne sortirait plus. Le piege est deja paye trois fois
 * dans ce pack.
 *
 * TON NEUTRE : le modele quitte la liste, il n'emporte rien avec lui. Le dire
 * est le seul moyen de retirer un modele sans se demander ce qui arrive aux
 * fiches qu'il a servi a creer.
 */
export interface TemplateRemoveDialogProps {
  open: boolean;
  /** Le modele vise. Il reste monte pendant le fondu de sortie du `Modal`. */
  modele: ModeleDeFiche;
  onCancel: () => void;
  onConfirm: () => void;
}

export function TemplateRemoveDialog({
  open,
  modele,
  onCancel,
  onConfirm,
}: TemplateRemoveDialogProps): JSX.Element {
  return (
    /* LES TEXTES VIENNENT DE LA FABRIQUE, jamais du JSX : le menu « ... » d'un
       modele ouvert en mode Modeles pose la MEME fenetre, depuis un fichier qui
       ne voit pas celui-ci. Voir `templateRemoveConfirm`. */
    <ConfirmDialog
      open={open}
      {...templateRemoveConfirm(modele.name)}
      onCancel={onCancel}
      onConfirm={onConfirm}
    />
  );
}

export default TemplateMenu;
